/* eslint-disable no-console */
/*eslint-env browser */
import React, { useEffect, useRef } from 'react'
import {
  Switch,
  Redirect,
  matchPath,
  useHistory,
  useLocation
} from 'react-router-dom'
import * as styles from './shell.styl'
import cx from 'classnames'
import { Account } from './components'
import { isPageByPathNameFromLocation } from '@src/utils/router.helpers'
import Header from './components/header'
import SidebarContainer from '../sidebar'
import { CSSSelector } from '@src/utils/css.selector.helper'
import { routes } from './configurations'
import { map, isEmpty, compact } from 'lodash'
import { AbortControllerRoute, Route } from './helpers'
import { toast } from 'react-toastify'
import { AppStorage } from '@src/utils/storage.helpers'
import { Routes } from '@src/constants'
import { storagePrefix } from '@src/constants/constants'
import { EVENT_TOKEN_EXPIRED } from '@src/utils/jwt.token'
import PolicyJsonContainer from '@src/components/common/expander.policies/policy.json'
import type { FilterAccount } from '@src/redux/reducers/filter'
import FeatureFlagsService, { FeatureFlagsContainer } from '../feature.flags'
import {
  getResourcesAccessSet,
  getSourcesAccessSet,
  useFeatureFlagsContext
} from '../feature.flags/context'
import IdentityNowSourcesCacheContainer from '../accounts/components/sources.cache'
import * as CommonSelectors from '@src/selectors'
import { useDispatch, useSelector } from 'react-redux'
import * as ShellActions from '@src/redux/actions/shell.actions'
import * as AuthActions from '@src/redux/actions/auth.actions'
import OnlineStatusProvider from '../online.status'
import { fetchUseChangesPageFlag } from '@src/redux/actions/changes.actions'
import {
  fetchTexBoxTooltipFixFlag,
  fetchUseIDNPathFlag
} from '@src/redux/actions/shell.actions'
import { pickBy } from 'lodash'

const storage = new AppStorage(storagePrefix, sessionStorage)

export const ShellCSSSelectors = {
  shell: new CSSSelector('shell'),
  dataLogged: 'data-logged',
  dataHasAccounts: 'data-has-accounts'
}

interface OwnProps {}

export type Props = OwnProps

interface State {
  sidebarCollapsed: boolean
  refresh: boolean
}

interface Routers {
  currentAccount: FilterAccount
}
const resouresAccessSet = getResourcesAccessSet()
const sourcesAccessSet = getSourcesAccessSet()

export const RegisterAccountsRouters: React.FC<Routers> = React.memo(
  ({ currentAccount }: Routers) => {
    const {
      CAM_TEMP_20210127_USE_REPORT_SERVICE,
      CAM_PERM_20220804_HIDE_RESOURCES_ACCESS,
      CAM_PERM_20220804_HIDE_ALL_SOURCE
    } = useFeatureFlagsContext()
    const useReportPage = CAM_TEMP_20210127_USE_REPORT_SERVICE?.response?.value
    const resourcesAccessHide =
      CAM_PERM_20220804_HIDE_RESOURCES_ACCESS?.response?.value ||
      CAM_PERM_20220804_HIDE_ALL_SOURCE?.response?.value
    const sourcesAccessHide = CAM_PERM_20220804_HIDE_ALL_SOURCE?.response?.value
    const filteredRoutes = pickBy(routes, (obj, key) => {
      if (resourcesAccessHide) {
        return !resouresAccessSet.has(key)
      }
      if (sourcesAccessHide) {
        return !sourcesAccessSet.has(key)
      }
      return true
    })

    return (
      <>
        {compact(
          map(filteredRoutes, (route, key) => {
            if (route === routes.reports && !useReportPage) return null
            return (
              <AbortControllerRoute
                key={key}
                {...route}
                currentAccount={currentAccount}
              />
            )
          })
        )}
      </>
    )
  }
)

export const NotRegisterAccountsRouters: React.FC<{}> = React.memo(() => (
  <Switch>
    <AbortControllerRoute
      path={routes.accounts.path}
      component={routes.accounts.component}
    />
    <Route
      exact={routes.setup.exact}
      path={routes.setup.path}
      component={routes.setup.component}
    />
  </Switch>
))

const Shell: React.FC<Props> = (props: Props) => {
  const history = useHistory()
  const location = useLocation()
  const currentAccount = useSelector(CommonSelectors.getCurrentAccount)
  const tenant = useSelector(CommonSelectors.getTenant)
  const isLoggedIn = useSelector(CommonSelectors.isLoggedIn)
  const resourceTypes = useSelector(CommonSelectors.getResourceTypes)
  const useIDNLoginPath = useSelector(CommonSelectors.getUseIDNLoginPage)
  const sidebarCollapsed = storage.get('camSidebarCollapsed') === true
  const { account_info = [] } = tenant || {}
  const hasAccounts = account_info && account_info.length > 0
  const hasResourceTypes = resourceTypes && !isEmpty(resourceTypes)
  const isSetupPage = isPageByPathNameFromLocation(
    routes.setup.path as string,
    location
  )
  const isAccounts = matchPath(location?.pathname, {
    path: [routes.accounts.path as string]
  })
  const shellDataAttributes = {
    [ShellCSSSelectors.dataLogged]: isLoggedIn,
    [ShellCSSSelectors.dataHasAccounts]: hasAccounts
  }
  const hasRegisterAccounts = isLoggedIn && hasAccounts

  const dispatch = useDispatch()

  const shellContainer = useRef<HTMLDivElement>(null)
  let unlisten: any = null

  const [state, setState] = React.useState<State>({
    sidebarCollapsed,
    refresh: false
  })

  useEffect(() => {
    toast.dismiss()
    init()
    applyListener()
    dispatch(fetchUseChangesPageFlag())
    dispatch(fetchUseIDNPathFlag())
    dispatch(fetchTexBoxTooltipFixFlag())

    return () => {
      removeListener()
    }
  }, [])

  function handleEventTokenIsExpired(event: Event) {
    history.push({
      pathname: Routes.loginPage,
      search: window.location.search
    })
  }

  function init() {
    dispatch(ShellActions.getSupportedResourceTypes())
    dispatch(AuthActions.restoreSession(true))
  }

  function applyListener() {
    unlisten = history.listen(location => {
      if (shellContainer && shellContainer?.current) {
        shellContainer?.current?.scrollTo(0, 0)
      }
    })

    document.addEventListener(
      EVENT_TOKEN_EXPIRED.type,
      handleEventTokenIsExpired,
      false
    )
  }

  function removeListener() {
    document.removeEventListener(
      EVENT_TOKEN_EXPIRED.type,
      handleEventTokenIsExpired,
      false
    )
    if (unlisten && typeof unlisten === 'function') {
      unlisten()
    }
  }

  function handleCollapse() {
    const newSidebarCollapsed = !state.sidebarCollapsed
    setState({
      ...state,
      sidebarCollapsed: newSidebarCollapsed
    })
    storage.set('camSidebarCollapsed', newSidebarCollapsed)
    window.dispatchEvent(new Event('resize'))
  }

  function onRefresh() {
    init()
    setState({ ...state, refresh: true })
    setTimeout(() => {
      setState({ ...state, refresh: false })
    })
  }

  // if (!storage.get('userEmail')) {
  //   return <Redirect to={Routes.loginPage} />
  // }

  if (isLoggedIn && useIDNLoginPath) {
    const { tenant_id } = tenant
    window.location.replace(`https://${tenant_id}.identitysoon.com`)
  }
  if (isLoggedIn && (!tenant || !hasAccounts) && !isSetupPage && !isAccounts) {
    return <Redirect to={routes.setup.path as string} />
  }
  return (
    <OnlineStatusProvider>
      <FeatureFlagsService>
        <IdentityNowSourcesCacheContainer>
          <div
            className={styles.main}
            id={ShellCSSSelectors.shell.value}
            {...shellDataAttributes}
          >
            <SidebarContainer
              {...props}
              collapsed={sidebarCollapsed}
              refresh={onRefresh}
            />
            <div
              ref={shellContainer}
              className={cx(styles.container, {
                collapsed: state.sidebarCollapsed
              })}
            >
              <Header
                history={history}
                handleCollapse={handleCollapse}
                sidebarCollapsed={state.sidebarCollapsed}
              />
              <FeatureFlagsContainer>
                {hasRegisterAccounts && hasResourceTypes && !state.refresh ? (
                  <>
                    <Switch>
                      <Route
                        exact
                        path={'/'}
                        component={() => <Account tenant={tenant} />}
                      />
                      <RegisterAccountsRouters
                        currentAccount={currentAccount}
                      />
                    </Switch>
                    <PolicyJsonContainer />
                  </>
                ) : null}
                {isLoggedIn && !hasAccounts ? (
                  <NotRegisterAccountsRouters />
                ) : null}
              </FeatureFlagsContainer>
            </div>
          </div>
        </IdentityNowSourcesCacheContainer>
      </FeatureFlagsService>
    </OnlineStatusProvider>
  )
}

export default Shell
